import { TabularCopySink } from './tabular.copysink';
import { Metamodel } from 'app/model/base/model';
import { AzurePostgreSqlTableDataset } from 'app/model/dataset/azure-postgre-sql-table.dataset';
import { IAttribute, IModel, IViolation } from 'app/model/base/annotation';
import { Model } from 'app/model/backendmodel';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { validateUniqueKeyColumns } from './copysink.utils';
import { GlobalVariables } from 'app/services/globals';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopyActivity } from '../copy.activity';
import { AzurePostgreSqlLinkedService } from 'app/model/linkedservice/azure-db-postgre.linkedservice';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import {
    format,
    ICopySinkWithUpdateMethod,
    IValidationContext,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import {
    validateIRVersion,
    azurePostgreSQLV2ScriptandUpsertVersion,
} from 'app/shared/utils/version.check.util';

@Metamodel.model({
    name: adfModelTypeMap.azurePostgreSQL.copySinkType,
    display: [],
    referable: true,
    tags: { datasetModel: [Metamodel.modelFor(AzurePostgreSqlTableDataset)] }
})
export class AzurePostgreSQLSink extends TabularCopySink implements ICopySinkWithUpdateMethod {
    public static readonly MODEL: IModel;
    public static readonly WRITE_METHOD: IAttribute;
    public static readonly WRITE_BEHAVIOR_KEYS: IAttribute;
    public static readonly UPSERT_SETTINGS: IAttribute;

    public nullableOriginalType?: string;

    @Metamodel.attribute({
        display: stringResources.Write_Method_Label,
        association: 'WRITE_METHOD',
        constraint: {
            validator: (owner, value, violation, context) =>
                AzurePostgreSQLSink.validateWriteMethod(owner as AzurePostgreSQLSink, value, violation, context)
        }
    })
    public writeMethod?: 'BulkInsert' | 'CopyCommand' | 'Upsert' = 'CopyCommand';

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_KEYS',
        display: stringResources.DataFlowDatasetKeyColumns,
        accepts: ['Array', 'Object'],
        constraint: {
            validator: (owner) => AzurePostgreSQLSink.validateKeyColumns(owner as AzurePostgreSQLSink)
        }
    })
    public keyColumns?: string[];

    // for backward compatiblity where metadata-driven template grouped all upsertSettings in 1
    @Metamodel.attribute({
        templatable: true,
        association: 'UPSERT_SETTINGS'
    })
    public upsertSettings?: Model.WriteBehaviorUpsertOptions;

    constructor(name?: string, description?: string, id?: number) {
        super(name, description, id);
        this.writeBatchSize = 1000000;
        this.writeBatchTimeout = '00:30:00';
    }

    // copy-job update method (Append/Upsert) <-> connector writeMethod
    public get updateMethod(): UpdateMethodType {
        switch (this.writeMethod) {
            case 'Upsert':
                return UpdateMethod.Upsert;
            default:
                return UpdateMethod.Append;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Upsert:
                this.writeMethod = 'Upsert';
                break;
            case UpdateMethod.Append:
                this.writeMethod = 'CopyCommand';
                break;
            default:
        }
    }

    public get upsertKeyColumns(): string[] {
        return this.keyColumns;
    }

    public set upsertKeyColumns(value: string[]) {
        this.keyColumns = value;
    }

    public static validateKeyColumns(sink: AzurePostgreSQLSink): IViolation[] {
        // only check for key columns mapping when certain write behavior is upsert
        if (ValueAdapter.toResource(sink, AzurePostgreSQLSink.WRITE_METHOD) !== 'Upsert') {
            return [];
        }
        return validateUniqueKeyColumns(sink, AzurePostgreSQLSink.UPSERT_SETTINGS, sink.keyColumns, !GlobalVariables.skipKeyColumnsForAzPG);
    }

    public static async validateWriteMethod(sink: AzurePostgreSQLSink, value: string, violation: IViolation, context: IValidationContext): Promise<IViolation[]> {
        if (ValueAdapter.toResource(sink, AzurePostgreSQLSink.WRITE_METHOD) !== 'Upsert') {
            return [];
        }

        const createViolation = (actualVersion: string, expertVersion: string, versionOwner: string): IViolation => ({
            context: sink,
            message: () => format(
                stringResources.Connector_Version_IsNotSupportedIn,
                versionOwner,
                format(stringResources.VersionFormat, actualVersion).toLocaleLowerCase(),
                stringResources.WriteBehavior_Option_Upsert.toLocaleLowerCase(),
                format(stringResources.VersionFormat, expertVersion).toLocaleLowerCase(),
            )
        });

        const violations = [];

        if (__IS_FABRIC_DPE__) {
            const sinkDataset: AzurePostgreSqlTableDataset = (sink.owner as CopyActivity).sinkDataset as AzurePostgreSqlTableDataset;
            if (sinkDataset?.version !== '2.0') {
                violations.push(createViolation('1.0', '2.0', stringResources.DestinationLabel.toLocaleLowerCase()));
            }
        } else {
            const linkedService: AzurePostgreSqlLinkedService = (sink.owner as CopyActivity).sinkDataset?.linkedService as AzurePostgreSqlLinkedService;
            if (linkedService?.version === driverVersions.v1) {
                violations.push(createViolation('1.0', '2.0', stringResources.LinkedService.toLocaleLowerCase()));
            }
            const integrationRuntime = linkedService?.integrationRuntime;
            const shirMeetRequirements = await validateIRVersion(integrationRuntime, azurePostgreSQLV2ScriptandUpsertVersion, 5, context);
            if (!shirMeetRequirements) {
                violations.push({
                    context: sink,
                    message: () => format(
                        stringResources.New_Connector_Required_IR,
                        stringResources.Upsert_Method.toLocaleLowerCase(),
                        azurePostgreSQLV2ScriptandUpsertVersion
                    )
                });
            }
        }
        return violations;
    }
}
